import React, { useEffect, useMemo, useState } from "react";
import {
  getAllInstallmentSettingsViaAdmin,
  createInstallmentSettingViaAdmin,
  UpdateInstallmentSettingViaAdmin,
  deleteInstallmentSettingViaAdmin,
  toggleInstallmentSettingViaAdmin,
  getAllProgramsViaAdmin,
} from "../../../../utils/Api";
import { toast } from "react-toastify";
import { LoadingSpinner, formatCurrency } from "../components/feesUtils";

// These plans split the one-time admission (application) fee only. Tuition /
// semester installments are a separate system, configured per batch in Fee
// Structures.

const blankRow = (n) => ({
  installmentNumber: n,
  percentage: 0,
  dueAfterDays: n === 1 ? 0 : 7 * (n - 1),
  description: `Installment ${n}`,
});

const defaultForm = () => ({
  program: "", // "" => Global (all programs)
  isEnabled: true,
  applicableToApplicationFees: true,
  planName: "2 Installments (50-50)",
  breakdown: [
    { installmentNumber: 1, percentage: 50, dueAfterDays: 0, description: "First Payment" },
    { installmentNumber: 2, percentage: 50, dueAfterDays: 7, description: "Second Payment" },
  ],
});

const InstallmentSettingsTab = () => {
  const [settings, setSettings] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(defaultForm());

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [sRes, pRes] = await Promise.all([
        getAllInstallmentSettingsViaAdmin(),
        getAllProgramsViaAdmin(),
      ]);
      setSettings(sRes?.data || []);
      setPrograms(pRes?.programs || []);
    } catch (error) {
      console.error("Error loading installment settings:", error);
      toast.error(error?.response?.data?.message || "Failed to load installment settings");
    } finally {
      setLoading(false);
    }
  };

  const programNameById = useMemo(() => {
    const m = new Map();
    for (const p of programs) m.set(String(p._id), p.programName || p.name);
    return m;
  }, [programs]);

  const percentTotal = form.breakdown.reduce(
    (sum, r) => sum + (Number(r.percentage) || 0),
    0
  );
  const percentValid = percentTotal === 100;

  const openCreate = () => {
    setEditingId(null);
    setForm(defaultForm());
    setShowModal(true);
  };

  const openEdit = (row) => {
    const plan = row.installmentPlans?.[0];
    setEditingId(row._id);
    setForm({
      program: row.program ? String(row.program) : "",
      isEnabled: !!row.isEnabled,
      applicableToApplicationFees: row.applicableToApplicationFees ?? true,
      planName: plan?.name || "Installment Plan",
      breakdown:
        plan?.breakdown?.length > 0
          ? plan.breakdown.map((b, i) => ({
              installmentNumber: i + 1,
              percentage: Number(b.percentage) || 0,
              dueAfterDays: Number(b.dueAfterDays) || 0,
              description: b.description || `Installment ${i + 1}`,
            }))
          : defaultForm().breakdown,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
  };

  const updateRow = (idx, field, value) => {
    setForm((f) => ({
      ...f,
      breakdown: f.breakdown.map((r, i) =>
        i === idx
          ? {
              ...r,
              [field]:
                field === "percentage" || field === "dueAfterDays"
                  ? value === ""
                    ? ""
                    : Number(value)
                  : value,
            }
          : r
      ),
    }));
  };

  const addRow = () => {
    setForm((f) => ({
      ...f,
      breakdown: [...f.breakdown, blankRow(f.breakdown.length + 1)],
    }));
  };

  const removeRow = (idx) => {
    setForm((f) => {
      if (f.breakdown.length <= 2) {
        toast.warning("A plan must have at least 2 installments");
        return f;
      }
      const next = f.breakdown
        .filter((_, i) => i !== idx)
        .map((r, i) => ({ ...r, installmentNumber: i + 1 }));
      return { ...f, breakdown: next };
    });
  };

  const handleSave = async () => {
    if (!percentValid) {
      toast.error(`Installment percentages must add up to 100% (currently ${percentTotal}%)`);
      return;
    }
    for (const r of form.breakdown) {
      if (!(Number(r.percentage) >= 1 && Number(r.percentage) <= 99)) {
        toast.error("Each installment percentage must be between 1 and 99");
        return;
      }
    }
    if (!form.applicableToApplicationFees) {
      toast.error("This plan must apply to the admission fee");
      return;
    }

    const programId = form.program || null;
    const payload = {
      program: programId,
      programName: programId ? programNameById.get(String(programId)) || "" : "",
      isEnabled: form.isEnabled,
      applicableToApplicationFees: form.applicableToApplicationFees,
      // Student-fee path was removed backend-side; keep the field pinned false so
      // the model stays satisfied without exposing the dead toggle.
      applicableToStudentFees: false,
      installmentPlans: [
        {
          planId: `${form.breakdown.length}-installments`,
          name: form.planName?.trim() || `${form.breakdown.length} Installments`,
          numberOfInstallments: form.breakdown.length,
          isDefault: true,
          breakdown: form.breakdown.map((r, i) => ({
            installmentNumber: i + 1,
            percentage: Number(r.percentage),
            dueAfterDays: Number(r.dueAfterDays) || 0,
            description: r.description || `Installment ${i + 1}`,
          })),
        },
      ],
      reminderSettings: {
        enableReminders: true,
        reminderDays: [
          { daysBefore: 7, reminderType: "upcoming", isActive: true },
          { daysBefore: 0, reminderType: "due_today", isActive: true },
          { daysBefore: -1, reminderType: "overdue", isActive: true },
        ],
      },
    };

    setSaving(true);
    try {
      if (editingId) {
        await UpdateInstallmentSettingViaAdmin({ ...payload, id: editingId });
        toast.success("Installment plan updated");
      } else {
        await createInstallmentSettingViaAdmin(payload);
        toast.success("Installment plan created");
      }
      closeModal();
      fetchAll();
    } catch (error) {
      console.error("Save error:", error);
      toast.error(error?.response?.data?.message || "Failed to save installment plan");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (row) => {
    setBusyId(row._id);
    try {
      await toggleInstallmentSettingViaAdmin({ id: row._id });
      toast.success(row.isEnabled ? "Disabled" : "Enabled");
      fetchAll();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to toggle");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (row) => {
    if (
      !window.confirm(
        `Delete the installment plan for ${
          row.program ? row.programName : "All Programs (Global)"
        }? This cannot be undone.`
      )
    ) {
      return;
    }
    setBusyId(row._id);
    try {
      await deleteInstallmentSettingViaAdmin({ id: row._id });
      toast.success("Installment plan deleted");
      fetchAll();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Admission Fee Installments</h2>
          <p className="text-gray-600 mt-1">
            Installment plans for the one-time admission (application) fee — per
            program or as a global default.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="px-4 py-2 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
        >
          New Plan
        </button>
      </div>

      <div className="mb-6 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
        Tuition and semester fee installments are set per batch in the Fee
        Structures tab, not here.
      </div>

      {/* Table */}
      {settings.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <i className="mdi mdi-credit-card-settings text-gray-400 text-3xl"></i>
          </div>
          <h3 className="text-lg font-medium text-gray-700 mb-2">
            No installment plans yet
          </h3>
          <p className="text-gray-500">
            The admission fee is collected in full until you add a plan.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-16">
                    #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Applies To
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Plan
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Installments
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {settings.map((row, i) => {
                  const b = row.installmentPlans?.[0]?.breakdown || [];
                  return (
                    <tr key={row._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600">{i + 1}</td>
                      <td className="px-4 py-3">
                        {row.program ? (
                          <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-700">
                            {row.programName ||
                              programNameById.get(String(row.program)) ||
                              "Program"}
                          </span>
                        ) : (
                          <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                            All Programs (Global)
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-800">
                        {row.installmentPlans?.[0]?.name || "-"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {b.length
                          ? `${b.length} (${b.map((x) => `${x.percentage}%`).join(" / ")})`
                          : "-"}
                      </td>
                      <td className="px-4 py-3">
                        {row.isEnabled ? (
                          <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                            Enabled
                          </span>
                        ) : (
                          <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
                            Disabled
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEdit(row)}
                            className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleToggle(row)}
                            disabled={busyId === row._id}
                            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors disabled:opacity-50 ${
                              row.isEnabled
                                ? "text-orange-600 bg-orange-50 hover:bg-orange-100"
                                : "text-green-600 bg-green-50 hover:bg-green-100"
                            }`}
                          >
                            {row.isEnabled ? "Disable" : "Enable"}
                          </button>
                          <button
                            onClick={() => handleDelete(row)}
                            disabled={busyId === row._id}
                            className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded hover:bg-red-100 transition-colors disabled:opacity-50"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-100 sticky top-0 bg-white">
              <h3 className="text-lg font-semibold text-gray-800">
                {editingId ? "Edit" : "New"} Installment Plan
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                &times;
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Applies To
                  </label>
                  <select
                    value={form.program}
                    onChange={(e) => setForm((f) => ({ ...f, program: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Programs (Global default)</option>
                    {programs.map((p) => (
                      <option key={p._id} value={p._id}>
                        {p.programName || p.name}
                        {p.applicationFee ? ` — ${formatCurrency(p.applicationFee)}` : ""}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    A program-specific plan overrides the global one for that program.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plan Name
                  </label>
                  <input
                    type="text"
                    value={form.planName}
                    onChange={(e) => setForm((f) => ({ ...f, planName: e.target.value }))}
                    placeholder="e.g. 2 Installments (50-50)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.applicableToApplicationFees}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        applicableToApplicationFees: e.target.checked,
                      }))
                    }
                    className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    Applies to the <strong>admission (application) fee</strong>
                  </span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer pt-2 border-t border-gray-200">
                  <input
                    type="checkbox"
                    checked={form.isEnabled}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, isEnabled: e.target.checked }))
                    }
                    className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    Enabled (offer this plan at checkout)
                  </span>
                </label>
              </div>

              {/* Breakdown */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-gray-700">
                    Installment Breakdown
                  </label>
                  <button
                    onClick={addRow}
                    className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
                  >
                    + Add Installment
                  </button>
                </div>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 w-14">
                          #
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 w-32">
                          Percentage
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 w-36">
                          Due After (days)
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">
                          Description
                        </th>
                        <th className="px-3 py-2 w-12"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {form.breakdown.map((r, idx) => (
                        <tr key={idx}>
                          <td className="px-3 py-2">
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                              {idx + 1}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <div className="relative">
                              <input
                                type="number"
                                min="1"
                                max="99"
                                value={r.percentage}
                                onChange={(e) => updateRow(idx, "percentage", e.target.value)}
                                className="w-full pr-7 pl-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
                                %
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              min="0"
                              value={r.dueAfterDays}
                              onChange={(e) => updateRow(idx, "dueAfterDays", e.target.value)}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              value={r.description}
                              onChange={(e) => updateRow(idx, "description", e.target.value)}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </td>
                          <td className="px-3 py-2 text-center">
                            {form.breakdown.length > 2 && (
                              <button
                                onClick={() => removeRow(idx)}
                                className="text-red-500 hover:text-red-700"
                                title="Remove"
                              >
                                <i className="mdi mdi-delete-outline text-lg"></i>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div
                  className={`mt-2 text-sm font-semibold ${
                    percentValid ? "text-green-600" : "text-red-600"
                  }`}
                >
                  Total: {percentTotal}% {percentValid ? "" : "(must equal 100%)"}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-4 border-t border-gray-100 sticky bottom-0 bg-white">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !percentValid}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? "Saving..." : editingId ? "Update Plan" : "Create Plan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstallmentSettingsTab;
